import React, { useCallback, useState } from 'react';
import { Col, Form, Input, Radio, RadioChangeEvent, Row, Tooltip } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ButtonActionPage } from '../../../atoms';
import { ACCESS_CODE } from '../../../../constants/define/translation';
import {
  SubscriptionDurationValue,
  SubscriptionFormGenerateMulti,
  SubscriptionPlanValue,
} from '../../../../types/accessCode';

interface FormGenerateMultiCodeProps {
  onGenerate: (values: SubscriptionFormGenerateMulti, onSuccess: () => void, onFailed: () => void) => void;
}
export const FormGenerateMultiCode = (props: FormGenerateMultiCodeProps) => {
  const { t } = useTranslation(ACCESS_CODE);
  const [form] = Form.useForm();
  const [isRandomDuration, setIsRandomDuration] = useState(true);
  const [generating, setGenerating] = useState(false);

  const resetFields = useCallback(() => {
    form.resetFields();
  }, [form]);

  const onFinish = useCallback(
    async (values: SubscriptionFormGenerateMulti) => {
      setGenerating(true);
      props.onGenerate(
        values,
        () => {
          resetFields();
          setGenerating(false);
        },
        () => {
          setGenerating(false);
        },
      );
    },
    [props.onGenerate, resetFields],
  );

  const handleOnChangeCheckDuration = useCallback((e: RadioChangeEvent) => {
    setIsRandomDuration(e.target.value === SubscriptionDurationValue.RANDOM);
  }, []);

  return (
    <>
      <Form layout="vertical" form={form} onFinish={onFinish} name="from-insights">
        <div className="form-content">
          <Form.Item
            name="quantity"
            label={t('form.quantity')}
            rules={[{ required: true, message: t('validate.quantityBlank') }]}
            initialValue={1}
          >
            <Input placeholder={t('form.quantity')} type="number" min={1} max={99999} />
          </Form.Item>
          <Form.Item
            label={t('form.subscriptionPlan')}
            name="subscriptionPlan"
            initialValue={SubscriptionPlanValue.RANDOM}
          >
            <Radio.Group style={fullWidth}>
              <Row>
                <Tooltip title={t('tooltip.planRandom')} placement="topLeft">
                  <Col span={8}>
                    <Radio value={SubscriptionPlanValue.RANDOM}>{t('plan.random')}</Radio>
                  </Col>
                </Tooltip>
                <Tooltip title={t('tooltip.planChampion')} placement="topLeft">
                  <Col span={8}>
                    <Radio value={SubscriptionPlanValue.CHAMPION}>{t('plan.champion')}</Radio>
                  </Col>
                </Tooltip>
              </Row>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={t('form.subscriptionDuration')}
            name="subscriptionDuration"
            initialValue={SubscriptionDurationValue.RANDOM}
          >
            <Radio.Group style={fullWidth} onChange={handleOnChangeCheckDuration}>
              <Row>
                <Tooltip title={t('tooltip.durationRandom')} placement="topLeft">
                  <Col span={12}>
                    <Radio value={SubscriptionDurationValue.RANDOM}>{t('duration.random')}</Radio>
                  </Col>
                </Tooltip>
                <Tooltip title={t('tooltip.inputRandom')} placement="topLeft">
                  <Col span={12}>
                    <Radio value={SubscriptionDurationValue.INPUT}>{t('duration.input')}</Radio>
                  </Col>
                </Tooltip>
              </Row>
            </Radio.Group>
          </Form.Item>
          {!isRandomDuration ? (
            <Row>
              <Col span={12} offset={12}>
                <Form.Item
                  name="inputDuration"
                  rules={[{ required: true, message: t('validate.subscriptionDurationBlank') }]}
                  className="input-duration"
                >
                  <Input placeholder={t('form.subscriptionDuration')} type="number" min={1} max={120} />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <Row>
              <Col span={12} offset={12}>
                <Form.Item name="inputDuration_hidden" className="input-duration input-invisibility">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Form.Item name="target" label={t('form.target')}>
            <Input placeholder={t('form.targetPlaceholder')} maxLength={50} />
          </Form.Item>
        </div>
        <ButtonActionPage
          text={t('btnExport')}
          id="1"
          loading={generating}
          disabled={generating}
          className="btn-submit-generate-access-code"
          htmlType="submit"
          icon={<ExportOutlined />}
        />
      </Form>
    </>
  );
};

const fullWidth = {
  width: '100%',
};
